import { FLAG_ASSETS_CDN } from '../worldcup.constants';

/**
 * ISO 3166-1 alpha-3 → flag path segment (alpha-2 or `gb-eng` / `gb-sct` / `gb-wls`).
 * If the API or a cached payload ever sends MEX/KOR/ZAF instead of mx/kr/za, we still resolve.
 */
const ISO_ALPHA3_TO_FLAGCDN_CODE: Record<string, string> = {
  alg: 'dz',
  arg: 'ar',
  aus: 'au',
  aut: 'at',
  bel: 'be',
  bih: 'ba',
  bra: 'br',
  cmr: 'cm',
  can: 'ca',
  cpv: 'cv',
  chl: 'cl',
  col: 'co',
  cod: 'cd',
  hrv: 'hr',
  cuw: 'cw',
  cze: 'cz',
  dnk: 'dk',
  ecu: 'ec',
  egy: 'eg',
  eng: 'gb-eng',
  fra: 'fr',
  deu: 'de',
  gha: 'gh',
  hti: 'ht',
  irq: 'iq',
  irn: 'ir',
  ita: 'it',
  civ: 'ci',
  jam: 'jm',
  jpn: 'jp',
  jor: 'jo',
  kor: 'kr',
  mex: 'mx',
  mar: 'ma',
  nld: 'nl',
  nzl: 'nz',
  nga: 'ng',
  nor: 'no',
  pan: 'pa',
  pry: 'py',
  per: 'pe',
  pol: 'pl',
  prt: 'pt',
  qat: 'qa',
  sau: 'sa',
  sct: 'gb-sct',
  sen: 'sn',
  srb: 'rs',
  zaf: 'za',
  esp: 'es',
  swe: 'se',
  che: 'ch',
  tun: 'tn',
  tur: 'tr',
  ukr: 'ua',
  usa: 'us',
  ury: 'uy',
  uzb: 'uz',
  wal: 'gb-wls',
};

export interface MatchLikeForFlagFingerprint {
  homeCountryCode: string;
  awayCountryCode: string;
}

/**
 * Stable key for when the *set* of flag country codes changes (not when match rows are replaced).
 * Use this to avoid re-prefetching after cache updates that only touch predictions, etc.
 */
export function countryCodesFingerprintFromMatches(
  matches: ReadonlyArray<MatchLikeForFlagFingerprint>,
): string {
  const set = new Set<string>();
  for (const m of matches) {
    if (m.homeCountryCode) set.add(m.homeCountryCode);
    if (m.awayCountryCode) set.add(m.awayCountryCode);
  }
  return [...set].sort().join('|');
}

const FLAG_EXTENSION_OVERRIDES: Record<string, string> = {
  eu: 'webp',
};

/** Builds a CDN URL for a country code (svg for most flags, webp for exceptions like eu). */
export function getFlagCdnUrl(countryCode: string): string | null {
  const normalized = normalizeCountryCodeForFlagCdn(countryCode);
  if (!normalized) return null;
  const ext = FLAG_EXTENSION_OVERRIDES[normalized] ?? FLAG_ASSETS_CDN.EXTENSION;
  return `${FLAG_ASSETS_CDN.BASE_URL}${FLAG_ASSETS_CDN.FLAGS_PATH}/${normalized}.${ext}`;
}

/**
 * Lowercase, trim; supports:
 * - ISO 3166-1 alpha-2 (`fr`, `us`)
 * - UK subdivisions used by flagcdn (`gb-eng`, `gb-sct`, `gb-wls`)
 */
export function normalizeCountryCodeForFlagCdn(countryCode: string): string | null {
  const trimmed = countryCode.trim().toLowerCase();
  if (trimmed === 'xx') return null;

  if (/^[a-z]{3}$/.test(trimmed)) {
    const fromAlpha3 = ISO_ALPHA3_TO_FLAGCDN_CODE[trimmed];
    if (fromAlpha3 != null) {
      return normalizeCountryCodeForFlagCdn(fromAlpha3);
    }
  }

  if (!/^[a-z]{2}(-[a-z]{3})?$/.test(trimmed)) return null;
  return trimmed;
}
